import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 focus-visible:ring-offset-2 focus-visible:ring-offset-canvas disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default:
          "btn-shimmer relative overflow-hidden bg-gradient-to-br from-brand-400 via-brand-500 to-brand-700 text-white shadow-brand-soft ring-1 ring-brand-300/40 hover:from-brand-300 hover:via-brand-500 hover:to-brand-600 hover:scale-[1.01]",
        destructive:
          "bg-danger-500 text-white hover:bg-danger-700",
        outline:
          "border border-line bg-surface text-ink-700 hover:border-brand-300 hover:text-brand-700",
        secondary:
          "border border-line bg-surface-muted text-ink-900 hover:bg-canvas",
        ghost:
          "text-ink-700 hover:bg-ink-300/20 hover:text-ink-950 dark:hover:bg-white/[0.06] dark:hover:text-white",
        link:
          "text-brand-700 underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-lg px-3",
        lg: "h-11 rounded-lg px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
